<template>
  <v-container>
    <v-tabs v-model="activeTab" class="mb-6" color="primary">
      <v-tab to="/admin/forms">Formulaires</v-tab>
      <v-tab to="/admin/alerts">Alertes</v-tab>
      <v-tab to="/admin/communiques">Communiqués</v-tab>
      <v-spacer></v-spacer>
      <v-btn text @click="logout" class="mt-2 mr-2">
        Déconnexion <v-icon right small>mdi-logout</v-icon>
      </v-btn>
    </v-tabs>

    <v-card>
      <v-card-title>
        Gestion des Formulaires
        <v-spacer></v-spacer>
        <v-text-field
          v-model="search"
          append-icon="mdi-magnify"
          label="Rechercher"
          single-line
          hide-details
        ></v-text-field>
        <v-btn color="primary" class="ml-4" @click="openDialog()">
          <v-icon left>mdi-plus</v-icon>
          Nouveau
        </v-btn>
      </v-card-title>

      <v-data-table
        :headers="headers"
        :items="forms"
        :search="search"
        :loading="loading"
        loading-text="Chargement des données..."
      >
        <template v-slot:item.active="{ item }">
          <v-chip :color="item.active ? 'success' : 'grey'" small>
            {{ item.active ? 'Actif' : 'Inactif' }}
          </v-chip>
        </template>
        <template v-slot:item.actions="{ item }">
          <v-icon small class="mr-2" color="primary" @click="openDialog(item)">
            mdi-pencil
          </v-icon>
          <v-icon small color="error" @click="confirmDelete(item)">
            mdi-delete
          </v-icon>
        </template>
      </v-data-table>
    </v-card>

    <!-- Form Dialog -->
    <v-dialog v-model="dialog" max-width="900px" persistent>
      <v-card>
        <v-card-title>
          <span class="headline">{{ formTitle }}</span>
        </v-card-title>

        <v-card-text>
          <v-container>
            <v-row>
              <v-col cols="12" md="6">
                <v-text-field v-model="editedItem.title" label="Titre Principal"></v-text-field>
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field v-model="editedItem.name" label="Nom (Interne)"></v-text-field>
              </v-col>
              <v-col cols="12" md="4">
                <v-text-field v-model="editedItem.customerId" label="Customer ID"></v-text-field>
              </v-col>
              <v-col cols="12" md="4">
                <v-text-field v-model="editedItem.operationId" label="Operation ID"></v-text-field>
              </v-col>
              <v-col cols="12" md="4">
                 <v-text-field v-model="editedItem.serverPostUrl" label="Server Post URL"></v-text-field>
              </v-col>
              <v-col cols="12">
                <v-checkbox v-model="editedItem.active" label="Actif"></v-checkbox>
              </v-col>
              
              <v-col cols="12">
                <div class="subtitle-2 mb-2">Description (JSON Editor)</div>
                <div style="height: 300px;">
                  <vue-json-editor
                    v-if="dialog"
                    v-model="editedItem.description"
                    :show-btns="false"
                    :mode="'code'"
                    @json-change="onDescriptionChange"
                  ></vue-json-editor>
                </div>
                <div v-if="jsonErrors.description" class="error--text caption ml-1 mt-1">{{ jsonErrors.description }}</div>
              </v-col>

              <v-col cols="12">
                <div class="subtitle-2 mb-2">Champs (JSON Editor)</div>
                <div style="height: 400px;">
                  <vue-json-editor
                    v-if="dialog"
                    v-model="editedItem.fields"
                    :show-btns="false"
                    :mode="'code'"
                    @json-change="onFieldsChange"
                  ></vue-json-editor>
                </div>
                <div v-if="jsonErrors.fields" class="error--text caption ml-1 mt-1">{{ jsonErrors.fields }}</div>
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey darken-1" text @click="closeDialog">Annuler</v-btn>
          <v-btn color="primary" :loading="saveLoading" @click="save">Enregistrer</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete Confirm Dialog -->
    <v-dialog v-model="deleteDialog" max-width="400px">
      <v-card>
        <v-card-title class="headline">Confirmation</v-card-title>
        <v-card-text>Êtes-vous sûr de vouloir supprimer ce formulaire ?</v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey darken-1" text @click="deleteDialog = false">Non</v-btn>
          <v-btn color="error" @click="doDelete">Oui, Supprimer</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snackbar" :color="snackbarColor" timeout="3000">
      {{ snackbarText }}
    </v-snackbar>
  </v-container>
</template>

<script>
import axios from 'axios';
import vueJsonEditor from 'vue-json-editor';
// Import the required CSS for the JSON editor
import 'vue-json-editor/assets/jsoneditor.min.css';

export default {
  name: 'AdminForms',
  components: {
    vueJsonEditor
  },
  data: () => ({
    search: '',
    loading: false,
    saveLoading: false,
    dialog: false,
    deleteDialog: false,
    headers: [
      { text: 'Titre', value: 'title' },
      { text: 'Customer ID', value: 'customerId' },
      { text: 'Operation ID', value: 'operationId' },
      { text: 'Actif', value: 'active' },
      { text: 'Actions', value: 'actions', sortable: false, align: 'end' },
    ],
    forms: [],
    editedIndex: -1,
    editedItem: {
      title: '',
      name: '',
      customerId: '',
      operationId: '',
      serverPostUrl: '/post',
      active: true,
      description: {},
      fields: {}
    },
    defaultItem: {
      title: '',
      name: '',
      customerId: '',
      operationId: '',
      serverPostUrl: '/post',
      active: true,
      description: { type: 'html', content: '' },
      fields: {}
    },
    descriptionJson: '',
    fieldsJson: '',
    jsonErrors: {
      description: '',
      fields: ''
    },
    itemToDelete: null,
    snackbar: false,
    snackbarText: '',
    snackbarColor: 'success',
  }),

  computed: {
    formTitle() {
      return this.editedIndex === -1 ? 'Nouveau Formulaire' : 'Modifier le Formulaire';
    },
  },

  mounted() {
    this.fetchForms();
  },

  methods: {
    getHeaders() {
      const token = localStorage.getItem('adminToken');
      return {
        headers: { 'x-access-token': token }
      };
    },

    async fetchForms() {
      this.loading = true;
      try {
        const response = await axios.get(`${process.env.VUE_APP_URL}admin/api/forms`, this.getHeaders());
        this.forms = response.data.forms;
      } catch (err) {
        const message = (err.response && err.response.data && err.response.data.message) || 'Erreur lors de la récupération';
        this.showSnackbar(message, 'error');
        if (err.response && err.response.status === 401) this.logout();
      } finally {
        this.loading = false;
      }
    },

    openDialog(item) {
      if (item) {
        this.editedIndex = this.forms.indexOf(item);
        this.editedItem = JSON.parse(JSON.stringify(item)); // Deep clone to avoid direct mutation
      } else {
        this.editedIndex = -1;
        this.editedItem = JSON.parse(JSON.stringify(this.defaultItem));
      }
      this.jsonErrors = { description: '', fields: '' };
      this.dialog = true;
    },

    closeDialog() {
      this.dialog = false;
      this.jsonErrors = { description: '', fields: '' };
    },

    onDescriptionChange() {
       this.jsonErrors.description = '';
    },

    onFieldsChange() {
       this.jsonErrors.fields = '';
    },

    async save() {
      // Basic validation: ensure objects are valid (the editor usually handles this, but let's be safe)
      if (!this.editedItem.description || typeof this.editedItem.description !== 'object') {
          this.jsonErrors.description = 'Description doit être un objet JSON valide';
          return;
      }
      if (!this.editedItem.fields || typeof this.editedItem.fields !== 'object') {
          this.jsonErrors.fields = 'Champs doit être un objet JSON valide';
          return;
      }

      this.saveLoading = true;
      try {
        if (this.editedIndex > -1) {
          const id = this.editedItem._id;
          await axios.put(`${process.env.VUE_APP_URL}admin/api/forms/${id}`, this.editedItem, this.getHeaders());
          this.showSnackbar('Formulaire mis à jour');
        } else {
          await axios.post(`${process.env.VUE_APP_URL}admin/api/forms`, this.editedItem, this.getHeaders());
          this.showSnackbar('Formulaire créé');
        }
        this.fetchForms();
        this.closeDialog();
      } catch (err) {
        const message = (err.response && err.response.data && err.response.data.message) || 'Erreur lors de l’enregistrement';
        this.showSnackbar(message, 'error');
      } finally {
        this.saveLoading = false;
      }
    },

    confirmDelete(item) {
      this.itemToDelete = item;
      this.deleteDialog = true;
    },

    async doDelete() {
      try {
        await axios.delete(`${process.env.VUE_APP_URL}admin/api/forms/${this.itemToDelete._id}`, this.getHeaders());
        this.showSnackbar('Formulaire supprimé');
        this.fetchForms();
      } catch (err) {
        const message = (err.response && err.response.data && err.response.data.message) || 'Erreur lors de la suppression';
        this.showSnackbar(message, 'error');
      } finally {
        this.deleteDialog = false;
        this.itemToDelete = null;
      }
    },

    showSnackbar(text, color = 'success') {
      this.snackbarText = text;
      this.snackbarColor = color;
      this.snackbar = true;
    },

    logout() {
      localStorage.removeItem('adminToken');
      this.$router.push({ name: 'AdminLogin' });
    }
  },
};
</script>
